import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { SeverityIcon } from "../../components/primitives";
import { toneText, type Tone } from "./tone";

/**
 * One headline number with its label and a short context line. Problems colour the value and the
 * outline and add an icon (never colour alone); OK and neutral states stay calm.
 */
export function StatTile({
  label,
  value,
  sub,
  tone = "neutral",
  hint,
  size = "md",
  className,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  hint?: string;
  size?: "sm" | "md";
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      title={hint}
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-md border px-3 py-2",
        tone === "nok" ? "border-red-500/40 bg-red-500/10" : tone === "warn" ? "border-amber-500/35" : "border-border/70",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5 text-[11px] leading-tight text-muted-foreground">
        {tone === "nok" && <SeverityIcon severity="critical" className="h-3.5 w-3.5" />}
        {tone === "warn" && <SeverityIcon severity="warning" className="h-3.5 w-3.5" />}
        <span className="min-w-0 break-words">{label}</span>
      </div>
      <div
        className={cn(
          "min-w-0 break-words font-semibold leading-tight",
          size === "sm" ? "text-base" : "text-lg",
          toneText(tone),
        )}
      >
        {value}
      </div>
      {sub && <div className="min-w-0 break-words text-[11px] leading-snug text-muted-foreground">{sub}</div>}
      {children}
    </div>
  );
}

/**
 * Label / value rows that flow into as many columns as the container fits (no viewport breakpoints, so
 * it works inside narrow cards too). Values wrap instead of being cut.
 */
export function FactList({
  items,
  className,
  min = "12rem",
}: {
  items: { label: ReactNode; value: ReactNode; tone?: Tone; hint?: string }[];
  className?: string;
  min?: string;
}) {
  return (
    <dl
      className={cn("grid gap-x-6", className)}
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}), 1fr))` }}
    >
      {items.map((item, i) => (
        <div
          key={i}
          title={item.hint}
          className="flex min-w-0 items-baseline justify-between gap-3 border-b border-border/50 py-1.5"
        >
          <dt className="shrink-0 text-xs text-muted-foreground">{item.label}</dt>
          <dd className={cn("min-w-0 break-words text-right font-mono text-xs tabular-nums", toneText(item.tone))}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Small heading inside a section card. */
export function SubHeading({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</h4>
      {actions}
    </div>
  );
}

/** Coloured identity mark of a series (the text next to it stays in the foreground ink). */
export function SeriesSwatch({ color, className }: { color: string; className?: string }) {
  return <span className={cn("inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]", className)} style={{ backgroundColor: color }} aria-hidden />;
}
